import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { PoolRule } from '../api/types';
import { Badge, TypeBadge } from '../components/Badge';
import { Card, PageHeader } from '../components/Card';
import { ErrorBanner, LoadingBlock } from '../components/Feedback';
import { Button, Field, Input, Toggle } from '../components/Form';
import { Modal } from '../components/Modal';
import { DataTable, Mono } from '../components/Table';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { ago, num } from '../lib/format';

export function PoolPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const path = '/admin-api/pool';
  const query = useQuery<{ enabled: boolean; list: PoolRule[] }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
    refetchInterval: 15_000,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [expiry, setExpiry] = useState('0');
  const [prefix, setPrefix] = useState('');
  const [minStock, setMinStock] = useState('5');
  const [maxStock, setMaxStock] = useState('20');
  const [batchSize, setBatchSize] = useState('10');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingGlobal, setTogglingGlobal] = useState(false);

  const rules = query.data?.list ?? [];

  const create = async () => {
    setSaving(true);
    try {
      await api.post('/admin-api/pool', {
        project_id: Number(projectId),
        expiry: Number(expiry) || 0,
        prefix: prefix.trim() || null,
        min_stock: Number(minStock) || 5,
        max_stock: Number(maxStock) || 20,
        batch_size: Number(batchSize) || 10,
        enabled,
      });
      toast.success('Правило пула создано');
      setCreateOpen(false);
      setProjectId(''); setExpiry('0'); setPrefix(''); setMinStock('5'); setMaxStock('20'); setBatchSize('10'); setEnabled(true);
      void invalidatePrefix(queryClient, '/admin-api/pool');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const updateRule = async (rule: PoolRule, patch: Partial<PoolRule>) => {
    try {
      await api.patch(`/admin-api/pool/${rule.id}`, {
        min_stock: patch.min_stock,
        max_stock: patch.max_stock,
        batch_size: patch.batch_size,
        enabled: patch.enabled,
      });
      toast.success('Обновлено');
      void invalidatePrefix(queryClient, '/admin-api/pool');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const remove = async (rule: PoolRule) => {
    const ok = await confirm({
      title: 'Удалить правило?',
      body: `Правило для «${rule.project}» будет удалено.`,
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/admin-api/pool/${rule.id}`);
      toast.success('Правило удалено');
      void invalidatePrefix(queryClient, '/admin-api/pool');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const toggleGlobal = async (value: boolean) => {
    setTogglingGlobal(true);
    try {
      await api.patch('/admin-api/settings', { pool: { enabled: value } });
      toast.success(value ? 'Пул включён' : 'Пул выключен');
      void invalidatePrefix(queryClient, '/admin-api/pool');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setTogglingGlobal(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Пул номеров"
        description="Предзаготовленные номера для быстрой выдачи"
        actions={<Button variant="primary" onClick={() => setCreateOpen(true)}>Создать правило</Button>}
      />

      <Card title="Глобальный переключатель">
        {query.data ? (
          <div className="flex items-center gap-3">
            <Toggle checked={query.data.enabled} onChange={toggleGlobal} disabled={togglingGlobal} label={query.data.enabled ? 'Пул включён' : 'Пул выключен'} />
          </div>
        ) : <LoadingBlock />}
      </Card>

      <Card title="Правила" noPadding>
        {query.error ? (
          <div className="p-3"><ErrorBanner message={query.error.message} onRetry={() => query.refetch()} /></div>
        ) : query.isLoading ? <LoadingBlock /> : rules.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-zinc-500">Правил нет. Создайте первое.</div>
        ) : (
          <DataTable<PoolRule>
            columns={[
              { key: 'project', header: 'Проект', render: (r) => (
                <div className="flex items-center gap-2">
                  <TypeBadge type={r.type} />
                  <span className="truncate">{r.project}</span>
                </div>
              ) },
              { key: 'expiry', header: 'Expiry', align: 'right', hide: 'sm', width: '4rem', render: (r) => num(r.expiry) },
              { key: 'prefix', header: 'Prefix', hide: 'md', render: (r) => r.prefix ? <Mono>{r.prefix}</Mono> : '—' },
              { key: 'min_stock', header: 'Мин.', align: 'right', width: '4rem', render: (r) => num(r.min_stock) },
              { key: 'max_stock', header: 'Макс.', align: 'right', width: '4rem', render: (r) => num(r.max_stock) },
              { key: 'batch_size', header: 'Батч', align: 'right', hide: 'sm', width: '4rem', render: (r) => num(r.batch_size) },
              { key: 'available', header: 'Доступно', align: 'right', width: '5rem', render: (r) => <span className={r.available < r.min_stock ? 'text-red-400' : 'text-emerald-400'}>{num(r.available)}</span> },
              { key: 'enabled', header: 'Вкл', width: '4rem', align: 'center', render: (r) => <Badge tone={r.enabled ? 'success' : 'muted'}>{r.enabled ? 'да' : 'нет'}</Badge> },
              { key: 'last_refill', header: 'Посл. пополн.', align: 'right', hide: 'lg', width: '7rem', render: (r) => r.last_refill_at ? ago(r.last_refill_at) : '—' },
              { key: 'actions', header: '', width: '5rem', align: 'right', render: (r) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => updateRule(r, { enabled: !r.enabled })}>{r.enabled ? 'Выкл' : 'Вкл'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)}>Удалить</Button>
                </div>
              ) },
            ]}
            rows={rules}
            rowKey={(r) => r.id}
            loading={query.isFetching}
            minWidth="38rem"
          />
        )}
      </Card>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Создать правило пула" size="md"
        footer={<><Button variant="ghost" onClick={() => setCreateOpen(false)}>Отмена</Button><Button variant="primary" loading={saving} onClick={create}>Создать</Button></>}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="ID проекта" className="sm:col-span-2"><Input value={projectId} onChange={(e) => setProjectId(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Expiry"><Input value={expiry} onChange={(e) => setExpiry(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Prefix"><Input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="нет" /></Field>
          <Field label="Мин. запас"><Input value={minStock} onChange={(e) => setMinStock(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Макс. запас"><Input value={maxStock} onChange={(e) => setMaxStock(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Размер батча"><Input value={batchSize} onChange={(e) => setBatchSize(e.target.value)} inputMode="numeric" /></Field>
          <div className="sm:col-span-2"><Toggle checked={enabled} onChange={setEnabled} label="Включено" /></div>
        </div>
      </Modal>
    </div>
  );
}
